import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Copy, Mail, Smartphone } from 'lucide-react';

import { useIntake } from '../context/IntakeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * The "come back to this later" panel.
 *
 * Three ways back in, because each one fails for somebody: a QR code (best
 * when the customer is on a laptop and the car is outside), a copyable link,
 * and an email.
 *
 * The link and QR are shown whether or not the email sends, and lead rather
 * than hide behind the email field — this panel appears precisely when email
 * has already proved unreliable (see DoneStep), so the routes that don't
 * depend on it go first.
 */
const ResumeLinkPanel = ({ title = 'Add photos later' }) => {
  const { resumeUrl, form, emailResumeLink } = useIntake();

  const [qr, setQr] = useState('');
  const [email, setEmail] = useState(form.email || '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!resumeUrl) return;
    QRCode.toDataURL(resumeUrl, { margin: 1, width: 220 })
      .then(setQr)
      .catch(() => setQr(''));
  }, [resumeUrl]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(resumeUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — press and hold the link to copy it');
    }
  };

  const handleEmail = async () => {
    if (!email.trim()) return;

    setSending(true);
    try {
      const result = await emailResumeLink(email.trim());
      if (result.sent) {
        setSent(true);
        toast.success('Sent — check your inbox');
      } else {
        toast.info("We couldn't send the email. Please save the link or scan the code above.");
      }
    } catch (err) {
      toast.error(err.message || 'Could not send the email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-muted/50 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-medium">{title}</p>
        <span className="text-xs text-muted-foreground">Works for 30 days</span>
      </div>

      {qr && (
        <div className="flex items-center gap-4">
          <img src={qr} alt="QR code linking back to this request" className="h-28 w-28 rounded-lg bg-white p-1.5" />
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Smartphone className="mt-0.5 h-4 w-4 shrink-0" />
            Scan to carry on with your phone.
          </p>
        </div>
      )}

      <div className="flex gap-2">
        <Input readOnly value={resumeUrl} className="font-mono text-xs" onFocus={(event) => event.target.select()} />
        <Button type="button" variant="outline" size="icon" onClick={handleCopy} aria-label="Copy link">
          <Copy className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex gap-2">
        <Input
          type="email"
          inputMode="email"
          placeholder="you@example.com"
          value={email}
          onChange={(event) => { setEmail(event.target.value); setSent(false); }}
        />
        <Button type="button" variant="outline" onClick={handleEmail} disabled={sending || !email.trim()}>
          <Mail className="h-4 w-4" />
          {sent ? 'Sent' : 'Email it'}
        </Button>
      </div>
    </div>
  );
};

export default ResumeLinkPanel;
